import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import * as _ from 'lodash';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {RoutingRuleApiService} from '../../../services/api/routing/routing-rule/routing-rule-api.service';
import {WindowActionsService} from '../../../services/common/window-actions/window-actions.service';
import {RoleTypeEnum} from '../../../services/enums/role-type.enum';

@Component({
  selector: 'routing-rules-panel',
  templateUrl: './routing-rules-panel.component.html',
  styleUrls: ['./routing-rules-panel.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RoutingRulesPanelComponent implements OnInit {

  @Input()
  rules: any = [];

  @Input()
  selectedRule: any;

  @Input()
  selectedGroup: any;

  @Input()
  externalData: any;

  @Output()
  selectChanged = new EventEmitter<any>();

  @Output()
  deleteRequested = new EventEmitter<any>();

  @Output()
  editRequested = new EventEmitter<any>();

  @Output()
  moveRequested = new EventEmitter<any>();

  isUnauthorized: boolean;


/*  itemActions = {
    toolbarActions: [
      {actionIconClass: 'fa fa-trash-o', action: (event, rule) => this.onDeleteRule(event, rule)},
      {actionIconClass: 'fa fa-pencil', action: (event, rule) => this.onEditRule(event, rule)},
      {actionIconClass: 'fa fa-arrows', action: (event, rule) => this.onMoveRule(event, rule)}
    ]
  };*/

  constructor(private userPrivilege: UserPrivilegeService,
              private routingRuleApiService: RoutingRuleApiService) {
  }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized(RoleTypeEnum.ROUTING);
  }

  drop(event: CdkDragDrop<string[]>, viewPortInfo): void {
    if (this.isUnauthorized) { return; }

    const fromIndex = viewPortInfo.startIndexWithBuffer + event.previousIndex;
    const toIndex = viewPortInfo.startIndexWithBuffer + event.currentIndex;

    if (_.isEqual(toIndex, fromIndex)) { return; }

    this.moveItemInRules(fromIndex, toIndex);

    const dragObject = {
      nextId: this.rules[toIndex + 1] ? this.rules[toIndex + 1].id : -1,
      prevId: this.rules[toIndex - 1] ? this.rules[toIndex - 1].id : -1,
      item: this.rules[toIndex]
    };

    const formattedGroup = this.createRuleToDrag(dragObject);

    this.routingRuleApiService.editExistingRule(formattedGroup).then().catch(() => {
      this.moveItemInRules(toIndex, fromIndex);
    }).catch(() => {});
  }

  onDrag(): void {
    WindowActionsService.clearWindowSelections();
  }

  moveItemInRules(fromIndex, toIndex): void {
    moveItemInArray(this.rules, fromIndex, toIndex);
    this.rules = [...this.rules];
  }

  openHandler(rule): void {
    this.populateRuleSDAFields(rule, this.externalData);
  }

  selectHandler(rule): void {
    this.selectChanged.emit(rule);
  }

  isSelected(rule): boolean {
    if (rule && this.selectedRule) {
      return this.selectedRule.id === rule.id;
    }
    return false;
  }

  onDeleteRule(event, rule): void {
    this.deleteRequested.emit(rule);
  }

  onEditRule(event, rule): void {
    this.editRequested.emit(rule);
  }

  onMoveRule(event, rule): void {
    this.moveRequested.emit(rule);
  }

  toggleLiveMode(event, rule): void {
    event.stopPropagation();

    this.toggleModeByProperty(rule, 'adminState');
  }

  toggleTestMode(event, rule): void {
    event.stopPropagation();

    this.toggleModeByProperty(rule, 'testMode');
  }

  onClosed(item): void {
    item.open = false;
  }

  onOpened(item): void {
    item.open = true;
    this.openHandler(item);
  }

  private toggleModeByProperty(rule, type): void {
    rule[`${type}Boolean`] = !rule[`${type}Boolean`];

    rule[type] = (rule[type] === 'UNLOCKED') ? 'LOCKED' : 'UNLOCKED';
    this.routingRuleApiService.getRuleById(rule.id).then(fetchedRule => {
      fetchedRule[type] = rule[type];
      fetchedRule.prevRRId = null;
      fetchedRule.nextRRId = null;

      this.routingRuleApiService.editExistingRule(fetchedRule).then().catch(error => {
        rule[type] = rule[type] === 'UNLOCKED' ? 'LOCKED' : 'UNLOCKED';
        rule[`${type}Boolean`] = !rule[`${type}Boolean`];
      }).catch(() => {});
    });
  }

  private populateRuleSDAFields(rule, externalData): void {
    rule.source = this.restructureSource(rule, externalData);
    rule.destination = this.restructureDestination(rule, externalData);
    rule.action = this.restructureAction(rule, externalData);
    rule.advancedConditions = this.restructureAdvancedConditions(rule, externalData);
  }

  private restructureSource(rule, dataObject): any {
    const result: any = {};

    this.setField(rule.sourceNodes, result, 'nodes', dataObject.nodes.concat(dataObject.offlineNodes));
    this.setField(rule.sourcePcons, result, 'pcons', dataObject.pCons.concat(dataObject.offlinePCons));
    this.setField(rule.sourceReroutePcons, result, 'reroutePcons', dataObject.pCons.concat(dataObject.offlinePCons));
    this.setField(rule.sourceUserGroups, result, 'userGroups', dataObject.userGroups);
    this.setField(rule.sourceTopologyGroups, result, 'resourceGroups', dataObject.resourceGroups);
    this.setField(rule.sourceCustomers, result, 'sourceCustomers', dataObject.customers);
    result.allSourceCustomers = rule.allSourceCustomers;

    this.setRRAttributes(rule.sourceRRAttr, result, 'rrAttrs', dataObject.prefixGroups);

    return result;
  }

  private restructureDestination(rule, dataObject): any {
    const result: any = {};

    this.setField(rule.dstUserGroups, result, 'userGroups', dataObject.userGroups);
    this.setField(rule.destCustomers, result, 'destCustomers', dataObject.customers);
    result.allDestCustomers = rule.allDestCustomers;

    this.setRRAttributes(rule.destRRAttr, result, 'rrAttrs', dataObject.prefixGroups);

    return result;
  }

  private restructureAction(rule, dataObject): any {
    const result = {};

    if (_.isArray(rule.actions) && rule.actions.length > 0) {
      const mergedActions = this.mergedActionsByPriority(rule.actions);

      for (const prop of _.keys(mergedActions)) {
        result[prop] = [];
        result[prop].push.apply(result[prop], this.buildActionItem(mergedActions[prop], dataObject));
      }
    }

    return result;
  }

  private restructureAdvancedConditions(rule, dataObject): any {
    const result: any = {};

    this.setField(rule.tbrConditionsIds, result, 'timeConditions', dataObject.timeConditions);

    if (!_.isNil(rule.sipConditionGroupId)) {
      result.sipConditionGroup = dataObject.sipConditionGroups.find(scg => scg.id === rule.sipConditionGroupId);
    }

    return result;
  }

  private setField(srcObject, desObject, fieldParameter, dataArray): void {
    if (!_.isNil(srcObject)) {
      desObject[fieldParameter] = this.populateParameter(srcObject, dataArray);
    }
  }

  private populateParameter(originalParameter, objectItemList): any {
    const result = [];

    originalParameter.forEach((id) => {
      const item = objectItemList.find((val) => {
        return id === val.id;
      });

      if (item !== undefined) {
        result.push(item);
      }
    });

    return result;
  }

  private setRRAttributes(srcAttributes, dstAttributes, fieldParameter, dataArray): void {
    dstAttributes[fieldParameter] = {attributes: {host: [], prefix: []}, attributeGroups: []};

    if (_.isArray(srcAttributes.attributes) && srcAttributes.attributes.length > 0) {
      this.populateRRAttributes(srcAttributes.attributes, dstAttributes[fieldParameter].attributes);
    }

    if (_.isArray(srcAttributes.attributeGroups) && srcAttributes.attributeGroups.length > 0) {
      this.populateRRAttributesGroups(srcAttributes.attributeGroups, dstAttributes[fieldParameter], dataArray);
    }
  }

  private populateRRAttributes(srcRRAttributes, dstRRAttributes): void {
    srcRRAttributes.forEach((value) => {
      if (value.type === 'PREFIX') {
        dstRRAttributes.prefix.push(value.value);
      } else {
        dstRRAttributes.host.push(value.value);
      }
    });
  }

  private populateRRAttributesGroups(srcRRAttributes, dstRRAttributes, dataObject): void {
    srcRRAttributes.forEach((id) => {
      dstRRAttributes.attributeGroups.push(dataObject.find((val) => {
        return id === val.id;
      }));
    });
  }

  private mergedActionsByPriority(actions): any {
    const copiedActions = _.cloneDeep(actions);
    const copyLength = copiedActions.length;
    const result = {};
    let i;

    for (i = 0; i < copyLength; i++) {
      if (!(copiedActions[i].priority in result)) {
        result[copiedActions[i].priority] = [];
      }
    }

    for (i = 0; i < copyLength; i++) {
      result[copiedActions[i].priority].push(copiedActions[i]);
    }

    return result;
  }

  private buildActionItem(actionItem, optionLists): any {
    const actionItemCount = actionItem.length;
    const newActionObject = [];

    for (let i = 0; i < actionItemCount; i++) {
      newActionObject.push(this.populateActionProperties(actionItem[i], optionLists));
    }

    return newActionObject;
  }

  private populateActionProperties(action, dataObject): any {
    const newAction = {
      id: action.id,
      priority: action.priority,
      discard: action.discard,
      continueWithNodeInternalTables: action.continueWithNodeInternalTables,
      loopback: action.loopback,
      sipReason: action.sipReason,
      registeredUsers: action.registeredUsers,
      destTypeRequestUri: action.destTypeRequestUri,
      customerNormalizationSIPHeader: action.customerNormalizationSIPHeader,
      srcNormalizationFromHeader: action.srcNormalizationFromHeader,
      srcNormalizationPAIHeader: action.srcNormalizationPAIHeader,
      srcNormalizationPPIHeader: action.srcNormalizationPPIHeader
    };

    this.initializeActionField(newAction, 'node', action.nodeId, dataObject.nodes.concat(dataObject.offlineNodes));
    this.initializeActionField(newAction, 'pConnection', action.peerConnectionId, dataObject.pCons.concat(dataObject.offlinePCons));
    this.initializeActionField(newAction, 'voipPeer', action.voipPeerId, dataObject.voipPeers.concat(dataObject.offlineVoipPeers));
    this.initializeActionField(newAction, 'resourceGroup', action.topologyGroupId, dataObject.resourceGroups);

    this.initializeActionField(newAction, 'srcNormGroup', action.srcNormalizationGroupId, dataObject.attributeGroups);
    this.initializeActionField(newAction, 'dstNormGroup', action.destNormalizationGroupId, dataObject.attributeGroups);
    this.initializeActionField(newAction, 'sipManipulationGroup', action.sipManipulationGroupId, dataObject.sipManipulationGroups);

    this.setField(action.viaNodes, newAction, 'viaNodes', dataObject.nodes.concat(dataObject.offlineNodes));

    return newAction;
  }

  private initializeActionField(action, fieldName, id, array): any {
    if (!_.isNil(id)) {
      action[fieldName] = array.find((val) => {
        return id === val.id;
      });
    }
  }

  private createRuleToDrag(dragObject): any {
    return Object.assign({prevRRId: dragObject.prevId, nextRRId: dragObject.nextId}, dragObject.item);
  }

}
